import axios from 'axios';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { getApiScopes, msalInstance } from '../auth/msalConfig';
import { markSessionExpired } from '../auth/authStatus';
import { ApiError, isProblemDetails } from './problemDetails';
import { i18n } from '../i18n/config';

/** Sign-out is best-effort and tears the page down; exempt it from token
 * renewal side effects (raising the session-expired interstitial, reactive
 * retry) so a stale token at logout time doesn't pop a dialog mid-tear-down. */
const SIGN_OUT_PATH = '/auth/sign-out';

function isSignOutRequest(config?: { url?: string | null }): boolean {
  return typeof config?.url === 'string' && config.url.includes(SIGN_OUT_PATH);
}

interface AcquireOptions {
  /** Raise the `sessionExpired` signal when silent renewal needs interaction.
   *  Suppressed for sign-out (see {@link isSignOutRequest}). */
  raiseSessionExpiredSignal?: boolean;
  /** Force MSAL to mint a fresh token rather than serve the cached one — used
   *  by the reactive 401 retry so a rotated key / clock-skew rejection gets a
   *  genuinely new token before we give up. */
  forceRefresh?: boolean;
}

/**
 * Acquires an API access token from MSAL using the configured scopes. Returns
 * `null` when no account is signed in - the caller (request interceptor) then
 * omits the `Authorization` header so the request still flows (anonymous
 * endpoints, sign-in callbacks) instead of being short-circuited.
 *
 * When silent acquisition needs interaction (the session can't be renewed
 * without the user), it raises the `sessionExpired` signal and returns `null`
 * rather than auto-redirecting: the global `SessionExpiredHost` surfaces the
 * re-auth interstitial so the user re-initiates sign-in deliberately, instead
 * of being bounced to Entra mid-action (slice 02 goal, WEB PRD §2.3/§2.7).
 */
export async function acquireApiToken(
  instance: IPublicClientApplication = msalInstance,
  { raiseSessionExpiredSignal = true, forceRefresh = false }: AcquireOptions = {},
): Promise<string | null> {
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  const scopes = getApiScopes();
  if (!account || scopes.length === 0) {
    return null;
  }

  try {
    const result = await instance.acquireTokenSilent({ scopes, account, forceRefresh });
    return result.accessToken;
  } catch (error) {
    // Every silent-renewal failure is handled the same way: raise the signal and
    // return null, so the request still goes out (unauthenticated) and the 401 it
    // earns runs the recovery path that already exists.
    //
    // BUG-047: this used to rethrow anything that was not an
    // `InteractionRequiredAuthError`, which dead-ended the app. Silent renewal runs
    // in a hidden iframe against `login.microsoftonline.com`, and a browser that
    // refuses that third-party context fails with a `BrowserAuthError`
    // (`monitor_window_timeout`) or a `ClientAuthError` — not
    // `InteractionRequiredAuthError`. The rethrow escaped from the request
    // interceptor *before* axios issued anything, so: no HTTP request, nothing in
    // the network tab, nothing in server telemetry, and a rejection carrying no
    // `response`. `WhoamiBoundary` reads that as status 0, which is not 401, and
    // renders the generic error page — with no `sessionExpired` signal raised, so
    // nothing offers a way back. A reload just repeats it.
    //
    // This is the same defect BUG-015 fixed on the *response* side (see the 401
    // handler below, which names these error types explicitly); the request side
    // was left with the original narrow test.
    if (!(error instanceof InteractionRequiredAuthError)) {
      // Named explicitly: this class of failure is otherwise invisible — there is
      // no request to see in the network tab and no SPA telemetry (TD-336).
      console.warn('[auth] silent token acquisition failed; continuing unauthenticated', error);
    }
    if (raiseSessionExpiredSignal) {
      markSessionExpired();
    }
    return null;
  }
}

/** Axios request config tagged so the reactive 401 retry runs at most once. */
interface RetryableConfig extends InternalAxiosRequestConfig {
  __authRetried?: boolean;
}

/**
 * Raises the session-expired signal only when MSAL can no longer produce a token
 * without the user — no account cached, or silent acquisition refused. Used on
 * 403, where the status alone cannot distinguish a stale session from a genuine
 * permission denial: probing the session tells the two apart, so a real denial
 * keeps rendering its own error instead of looping through sign-in (BUG-015).
 *
 * Deliberately does not force a refresh: the question is whether the session is
 * still usable, not whether a newer token would be accepted.
 */
async function raiseIfSessionUnrecoverable(instance: IPublicClientApplication): Promise<void> {
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  if (!account || getApiScopes().length === 0) {
    markSessionExpired();
    return;
  }
  try {
    await instance.acquireTokenSilent({ scopes: getApiScopes(), account });
  } catch {
    markSessionExpired();
  }
}

/**
 * Factory for the shared axios instance used by every TanStack Query call.
 * Tests inject a synthetic MSAL instance to verify the bearer-injection
 * behavior without touching the real one.
 */
export function createApiClient(instance: IPublicClientApplication = msalInstance): AxiosInstance {
  const client = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const token = await acquireApiToken(instance, {
      raiseSessionExpiredSignal: !isSignOutRequest(config),
    });
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    } else {
      config.headers.delete('Authorization');
    }
    // Advertise the active UI language so the API can localize
    // server-rendered content (e.g. ProblemDetails, notifications) to match.
    // Read from i18next's resolved language; guarded in case it is unset
    // before i18n initializes.
    if (i18n.language) {
      config.headers.set('Accept-Language', i18n.language);
    }
    // The instance default is `application/json`, which is right for almost
    // every call — but a FormData body must be sent as
    // `multipart/form-data; boundary=…`, and only the platform can generate
    // that boundary. Leaving the JSON default in place produces a body no
    // server (or MSW) can parse. Deleting the header here lets the platform
    // set the correct one; a per-call override cannot, because axios keeps the
    // instance default when merging.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      config.headers.delete('Content-Type');
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const status = error.response?.status ?? 0;
      const original = error.config as RetryableConfig | undefined;

      // Reactive 401 recovery (WEB PRD §2.3/§2.7): the proactive per-request
      // acquisition above renews an *expired* access token before the call, so
      // a 401 here means MSAL still holds a token the API rejected (rotated
      // signing key, clock skew, revoked-then-reissued). Force a single fresh
      // token and retry the request exactly once. The `__authRetried` guard
      // caps it at one attempt, so a genuinely-blocked account (fresh token is
      // still refused) falls through after one retry instead of looping — the
      // WhoamiBoundary then shows the blocked dead-end. If the forced renewal
      // itself needs interaction, the session truly expired: raise the signal
      // and let it propagate to the re-auth interstitial. Sign-out is exempt.
      if (status === 401 && !isSignOutRequest(original)) {
        if (original?.__authRetried) {
          // Already retried with a genuinely fresh token and still refused. That
          // is a blocked or unknown account, not an expired session — raising the
          // interstitial here would loop the user through sign-in forever. Fall
          // through and let the WhoamiBoundary show the dead-end.
        } else {
          const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
          if (original && account && getApiScopes().length > 0) {
            try {
              const refreshed = await instance.acquireTokenSilent({
                scopes: getApiScopes(),
                account,
                forceRefresh: true,
              });
              original.__authRetried = true;
              original.headers.set('Authorization', `Bearer ${refreshed.accessToken}`);
              return client(original);
            } catch {
              // Any silent-renewal failure means the session cannot be repaired
              // without the user. Previously only InteractionRequiredAuthError
              // raised the interstitial, so a BrowserAuthError / ClientAuthError /
              // network failure fell through to a generic "couldn't load" panel
              // with a Try again button that could never succeed (BUG-015).
              markSessionExpired();
            }
          } else {
            // Nothing to renew with: MSAL holds no account. The token cache lives
            // in sessionStorage, so a restored tab or a timed-out session leaves
            // every request unauthenticated with no way back — this is the common
            // shape of the reported auth timeout (BUG-015).
            markSessionExpired();
          }
        }
      }

      // 403 is "authenticated but not allowed", so it must NOT be treated as an
      // expired session on its own — a user who genuinely lacks the role would be
      // bounced through sign-in indefinitely. Probe the session instead: only when
      // MSAL can no longer produce a token is this a stale session wearing a 403.
      // A healthy session leaves the error untouched for the caller to render.
      if (status === 403 && !isSignOutRequest(original)) {
        await raiseIfSessionUnrecoverable(instance);
      }

      const data = error.response?.data;
      if (isProblemDetails(data)) {
        return Promise.reject(new ApiError(status, data));
      }
      return Promise.reject(error);
    },
  );

  return client;
}

/**
 * Application-wide axios instance. Components should import this rather than
 * calling `createApiClient` directly so every request flows through the same
 * interceptor stack.
 */
export const apiClient = createApiClient();
